import { Component, DestroyRef, ElementRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, forkJoin } from 'rxjs';
import { ReviewerService } from '../../core/services/reviewer.service';
import { RebarAuthService } from '../../core/rebarauth/rebar.auth.service';
import { UserRoleService } from '../../core/services/user-role.service';
import { DateFormatService } from '../../core/services/date-format.service';
import { PeoplePickerComponent, IPeopleDetails } from '../../core/components/people-picker/people-picker.component';

@Component({
  selector: 'app-action-view-edit',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MatIconModule, PeoplePickerComponent],
  templateUrl: './action-view-edit.component.html',
  styleUrl: './action-view-edit.component.css',
})
export class ActionViewEditComponent implements OnInit {
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('statusField') statusFieldRef!: ElementRef<HTMLElement>;

  private destroyRef = inject(DestroyRef);
  private _activePicker: HTMLInputElement | null = null;
  private _pickerResizeHandler: (() => void) | null = null;
  private reviewerService = inject(ReviewerService);
  private rebarAuth = inject(RebarAuthService);
  private userRoleService = inject(UserRoleService);
  private route = inject(ActivatedRoute);
  private dateFormatSvc = inject(DateFormatService);

  actionPlanId = '';
  issueSearch = '';
  isLoading = false;
  isUpdating = false;
  dataLoaded = false;

  private reviewId: number | null = null;
  viewOnly = false;
  fromViewIcon = false;
  isGoLive = false;
  linkedIssuesTouched = false;
  isLinkedIssueDraftOrRejected = false;

  private originalValues = {
    actionPlanStatus: '',
    closedDate: '',
    internalDependenciesJson: '[]',
    revisedTargetDate: '',
    rcComments: '',
    redClientObservationDate: '',
    targetDateToInformClient: '',
    ownersJson: '[]',
    description: '',
    legacyApNumber: '',
    clientDependency: '',
    targetDate: '',
  };

  reviewSummary = {
    reviewId: '',
    reviewName: '',
    clientName: '',
    market: '',
    marketUnit: '',
    locationToBeReviewed: '',
    country: '',
    proposedMonth: '',
    planFieldworkStartDate: '',
    planFieldworkEndDate: '',
    rcReviewer: '',
    rcSupervisor: '',
    rcManager: '',
    statusOfReview: '',
    singleMultipleLocation: '',
    ScheduleFieldworkStartDate: '',
    ScheduleFieldworkEndDate: '',
  };

  internalDependencyOptions: string[] = [];
  internalDependencySearch = '';
  internalDependencyDropdownOpen = false;

  actionPlan = {
    description: '',
    closedDate: '',
    internalDependencies: [] as string[],
    targetDate: '',
    owners: [] as string[],
    actionPlanStatus: '',
    revisedTargetDate: '',
    rcComments: '',
    uploadedDocuments: [] as {
      documentId: number | null;
      documentType: string;
      uploadedBy: string;
      uploadedDate: string;
      isActive: boolean;
      description: string;
      fileName: string;
      storagePath: string;
    }[],
    clientDependency: 'yes' as 'yes' | 'no',
    redClientObservationDate: '',
    targetDateToInformClient: '',
    legacyApNumber: '',
  };

  linkedIssues: {
    id: string; title: string; description: string; rating: string;
    status: string; locationReviewed: string; linked: boolean;
  }[] = [];

  ownerPickerValue = '';
  filesToAdd: File[] = [];
  fileError = '';
  statusError = false;
  closedDateError = false;
  targetDateError = false;
  showToast = false;
  toastMessage = '';
  private toastTimer: any;

  readonly maxFileSizeBytes = 20 * 1024 * 1024;
  readonly allowedExtensions = [
    '.pdf', '.msg', '.xls', '.xlsx', '.doc', '.docx',
    '.txt', '.ppt', '.pptx', '.jpg', '.jpeg', '.png', '.zip',
  ];
  deletedDocuments: {
    documentId: number | null; documentType: string; uploadedBy: string;
    uploadedDate: string; isActive: boolean; description: string;
    fileName: string; storagePath: string;
  }[] = [];
  statusDropdownOpen = false;

  readonly statusOptions = ['Open', 'Open-Ageing & Static', 'Closed'];

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.statusDropdownOpen && !(event.target as HTMLElement).closest('.status-select')) {
      this.statusDropdownOpen = false;
    }
  }

  toggleStatusDropdown(): void {
    this.statusDropdownOpen = !this.statusDropdownOpen;
  }

  selectStatus(opt: string): void {
    this.actionPlan.actionPlanStatus = opt;
    this.statusDropdownOpen = false;
    this.statusError = false;
    this.targetDateError = false;
    if (!this.isClosedDateRequired) {
      // Back to a non-closed status: field becomes read-only, so discard any date entered meanwhile.
      this.closedDateError = false;
      this.actionPlan.closedDate = this.originalValues.closedDate;
    }
  }

  private isClosedStatus(status: string): boolean {
    const s = (status ?? '').trim().toLowerCase();
    return s === 'closed' || s === 'closed-blue not tracked';
  }

  /** Closed Date is mandatory only when the current status is Closed / Closed-Blue Not Tracked. */
  get isClosedDateRequired(): boolean {
    return this.isClosedStatus(this.actionPlan.actionPlanStatus);
  }

  /** Target Date is mandatory when editing a Draft/Rejected linked issue AP with Open or Open-Ageing & Static status. */
  get isTargetDateMandatory(): boolean {
    if (!this.isLinkedIssueDraftOrRejected) return false;
    const s = (this.actionPlan.actionPlanStatus ?? '').trim().toLowerCase();
    return s === 'open' || s === 'open-ageing & static';
  }

  /** Editable only while the status is being changed to Closed (already-Closed APs follow Closed AP rules).
   *  Exception: always editable when linked issue is Draft or Rejected. */
  get isClosedDateEditable(): boolean {
    if (this.isLinkedIssueDraftOrRejected) return true;
    return !this.viewOnly
      && this.isClosedDateRequired
      && !this.isClosedStatus(this.originalValues.actionPlanStatus);
  }

  onClosedDateChange(): void {
    if (this.actionPlan.closedDate) this.closedDateError = false;
  }

  ngOnInit(): void {
    this._pickerResizeHandler = () => {
      if (this._activePicker && !this._activePicker.disabled) {
        const picker = this._activePicker;
        picker.blur();
        setTimeout(() => picker.showPicker?.(), 0);
      }
    };
    window.addEventListener('resize', this._pickerResizeHandler);
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', this._pickerResizeHandler!));

    this.actionPlanId = this.route.snapshot.paramMap.get('actionPlanId') ?? '';
    const state = history.state as { reviewId?: number; viewOnly?: boolean; fromViewIcon?: boolean; isGoLive?: boolean };
    const qp = this.route.snapshot.queryParamMap;
    const qpReviewId = qp.get('reviewId');
    this.reviewId = state?.reviewId ?? (qpReviewId ? Number(qpReviewId) : null);
    this.viewOnly = state?.viewOnly ?? (qp.get('viewOnly') === 'true');
    this.fromViewIcon = state?.fromViewIcon === true || qp.get('fromViewIcon') === '1';
    this.isGoLive = state?.isGoLive === true || qp.get('isGoLive') === '1';
    if (this.reviewId == null) return;

    this.reviewerService.getInternalDependencies()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const list = Array.isArray(response) ? response : [];
          const deps: string[] = list
            .map((item: any) => {
              if (typeof item === 'string') return item;
              const val = item.InternalDependencyName ?? item.InternalDependency ?? item.DependencyName ?? item.Dependency ?? item.Name ?? item.Value ?? item.Text;
              if (val !== undefined && val !== null) return String(val);
              const firstStr = Object.values(item).find((v) => typeof v === 'string' && (v as string).trim().length > 0);
              return firstStr as string ?? null;
            })
            .filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
          if (deps.length) this.internalDependencyOptions = deps;
        },
        error: () => {},
      });

    this.isLoading = true;
    this.reviewerService.getActionPlansEAV(this.reviewId, Number(this.actionPlanId))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list: any) => {
          const arr = Array.isArray(list) ? list : [];
          const ap = arr.find((a: any) => String(a.ActionPlanID) === this.actionPlanId) ?? arr[0] ?? null;
          if (ap) {
            this.reviewSummary = {
              reviewId: String(this.reviewId),
              reviewName: ap.ReviewName ?? '',
              clientName: ap.Review_ClientName ?? ap.ClientName ?? '',
              market: ap.Review_Market ?? ap.Market ?? '',
              marketUnit: ap.Review_MarketUnit ?? ap.MarketUnit ?? '',
              locationToBeReviewed: ap.Review_LocationToBeReviewed ?? ap.Review_LocationName ?? '',
              country: ap.Review_DeliveryCountry ?? ap.DeliveryCountry ?? '',
              proposedMonth: ap.Review_ProposedMonth ?? ap.ProposedMonth ?? '',
              planFieldworkStartDate: ap.PlanFieldworkStartDate ?? '',
              planFieldworkEndDate: ap.PlanFieldworkEndDate ?? '',
              rcReviewer: ap.Review_RCReviewer ?? '',
              rcSupervisor: ap.Review_RCSupervisor ?? '',
              rcManager: ap.Review_RCManager ?? '',
              statusOfReview: ap.CurrentStatus ?? '',
              singleMultipleLocation: ap.Review_SingleMultipleLocation ?? ap.SingleMultipleLocation ?? '',
              ScheduleFieldworkStartDate: ap.Schedule_PlanFieldworkStartDate ?? ap.ScheduleFieldworkStartDate ?? ap.PlanFieldworkStartDate ?? '',
              ScheduleFieldworkEndDate: ap.Schedule_PlanFieldworkEndDate ?? ap.ScheduleFieldworkEndDate ?? ap.PlanFieldworkEndDate ?? '',
            };

            const rawOwners: string = ap.AP_OwnerEmail ?? ap.OwnerEmail ?? '';
            const owners = rawOwners ? rawOwners.split(/[,;]/).map((o: string) => o.trim()).filter(Boolean) : [];

            const docsRaw = ap.AP_DocumentsJSON ?? ap.DocumentsJSON ?? ap.Documents;
            const docsParsed = typeof docsRaw === 'string'
              ? (() => { try { return JSON.parse(docsRaw); } catch { return []; } })()
              : (Array.isArray(docsRaw) ? docsRaw : []);
            const uploadedDocuments = docsParsed.map((d: any) => ({
              documentId: d.DocumentID ?? null,
              documentType: d.DocumentType ?? '',
              uploadedBy: d.UploadedBy ?? '',
              uploadedDate: d.UploadedDate ?? '',
              isActive: d.IsActive !== false,
              description: d.Description ?? '',
              fileName: d.FileName ?? '',
              storagePath: d.StorageFilePath ?? '',
            }));

            this.actionPlan = {
              description: ap.AP_Description ?? ap.ActionPlanDescription ?? ap.Description ?? '',
              closedDate: this.parseToInputDate(ap.AP_ClosedDate ?? ap.ClosedDateForActionPlan ?? ap.ClosedDate ?? ''),
              internalDependencies: String(
                Object.prototype.hasOwnProperty.call(ap, 'AP_InternalDependency')
                  ? (ap.AP_InternalDependency ?? '')
                  : (ap['Internal dependency'] ?? ap.InternalDependency ?? ''),
              ).split(',').map((s: string) => s.trim()).filter(Boolean),
              targetDate: ap.AP_TargetDate ?? ap.TargetDateForActionPlanRemediationAction ?? ap.TargetDate ?? '',
              owners,
              actionPlanStatus: ap.Status || ap.AP_Status || ap.ActionPlanStatus || '',
              revisedTargetDate: this.parseToInputDate(ap.AP_RevisedTargetDate ?? ap.RevisedTargetDate ?? ap.RevisedTargetDateForActionPlan ?? ''),
              rcComments: ap.AP_RCComments ?? ap.RCComments ?? '',
              uploadedDocuments,
              clientDependency: (ap.AP_ClientDependency ?? ap.ClientDependency ?? '').toLowerCase() === 'yes' ? 'yes' : 'no',
              redClientObservationDate: this.parseToInputDate(ap.AP_RedClientDate ?? ap.RedClientDate ?? ap.RedClientObservationDate ?? ''),
              targetDateToInformClient: this.parseToInputDate(ap.AP_TargetDateClientInfo ?? ap.TargetDateClientInfo ?? ap.TargetDateclientinfo ?? ap.TargetDateToInformClient ?? ''),
              legacyApNumber: ap.AP_LegacyApNumber ?? ap.LegacyAPNumber ?? ap.LegacyApNumber ?? '',
            };

            // Build linked issues from IssueFields (rich data), falling back to LinkedIssueIDs/Names
            const issueFields: any[] = Array.isArray(ap.IssueFields) ? ap.IssueFields : [];
            if (issueFields.length > 0) {
              this.linkedIssues = issueFields.map((issue: any) => ({
                id: 'I-' + issue.IssueID,
                title: issue.IssueTitle ?? issue.IssueName ?? '',
                description: issue.IssueDescription ?? '',
                rating: issue.IssueRating ?? '',
                status: issue.IssueStatus ?? '',
                locationReviewed: issue.LocationReviewedImpacted ?? issue.LocationNames ?? '',
                linked: true,
              }));
            } else {
              const linkedIds = (ap.LinkedIssueIDs ?? '').split(',').map((id: string) => id.trim()).filter(Boolean);
              const linkedNames = (ap.LinkedIssueNames ?? '').split(',').map((n: string) => n.trim());
              this.linkedIssues = linkedIds.map((id: string, idx: number) => ({
                id: 'I-' + id,
                title: linkedNames[idx] ?? '',
                description: '',
                rating: '',
                status: '',
                locationReviewed: '',
                linked: true,
              }));
            }

            // Action plans linked to an Inactive (pending approval) issue are always read-only.
            if (this.linkedIssues.some(i => (i.status ?? '').toLowerCase().replace(/[^a-z]/g, '') === 'inactive')) {
              this.viewOnly = true;
            }

            // Action plans linked to a Draft or Rejected issue are fully editable,
            // unless the AP itself is Closed or Closed-Blue Not Tracked.
            if (!this.isClosedStatus(this.actionPlan.actionPlanStatus) && this.linkedIssues.some(i => {
              const s = (i.status ?? '').toLowerCase().replace(/[^a-z]/g, '');
              return s === 'draft' || s === 'rejected';
            })) {
              this.isLinkedIssueDraftOrRejected = true;
              this.viewOnly = false;
            }

            this.originalValues = {
              actionPlanStatus: this.actionPlan.actionPlanStatus,
              closedDate: this.actionPlan.closedDate,
              internalDependenciesJson: JSON.stringify(this.actionPlan.internalDependencies),
              revisedTargetDate: this.actionPlan.revisedTargetDate,
              rcComments: this.actionPlan.rcComments,
              redClientObservationDate: this.actionPlan.redClientObservationDate,
              targetDateToInformClient: this.actionPlan.targetDateToInformClient,
              ownersJson: JSON.stringify(owners),
              description: this.actionPlan.description,
              legacyApNumber: this.actionPlan.legacyApNumber,
              clientDependency: this.actionPlan.clientDependency,
              targetDate: this.actionPlan.targetDate,
            };
            this.dataLoaded = true;
          }
          this.isLoading = false;
        },
        error: (err) => {
          console.error('GetActionPlans EAV error:', err?.status, err?.message);
          this.isLoading = false;
        },
      });
  }

  get isAdmin(): boolean {
    return this.userRoleService.isAdmin;
  }

  get isLinkedIssuesDirty(): boolean {
    return this.isAdmin && this.linkedIssuesTouched;
  }

  get isDirty(): boolean {
    if (!this.dataLoaded) return false;
    return (
      this.actionPlan.actionPlanStatus !== this.originalValues.actionPlanStatus ||
      this.actionPlan.closedDate !== this.originalValues.closedDate ||
      JSON.stringify(this.actionPlan.internalDependencies) !== this.originalValues.internalDependenciesJson ||
      this.actionPlan.revisedTargetDate !== this.originalValues.revisedTargetDate ||
      this.actionPlan.rcComments !== this.originalValues.rcComments ||
      this.actionPlan.redClientObservationDate !== this.originalValues.redClientObservationDate ||
      this.actionPlan.targetDateToInformClient !== this.originalValues.targetDateToInformClient ||
      JSON.stringify(this.actionPlan.owners) !== this.originalValues.ownersJson ||
      this.actionPlan.description !== this.originalValues.description ||
      this.actionPlan.legacyApNumber !== this.originalValues.legacyApNumber ||
      this.actionPlan.clientDependency !== this.originalValues.clientDependency ||
      this.actionPlan.targetDate !== this.originalValues.targetDate ||
      this.filesToAdd.length > 0 ||
      this.deletedDocuments.length > 0
    );
  }

  onLinkedIssueChange(): void {
    this.linkedIssuesTouched = true;
  }

  get filteredIssues() {
    const isBlueNotTracked =
      this.actionPlan.actionPlanStatus?.toLowerCase() === 'closed-blue not tracked';

    let issues = isBlueNotTracked
      ? this.linkedIssues.filter(i => i.rating?.toLowerCase() === 'blue')
      : this.linkedIssues;

    const term = this.issueSearch.trim().toLowerCase();
    if (!term) return issues;
    return issues.filter(
      (issue) =>
        issue.id.toLowerCase().includes(term) ||
        issue.title.toLowerCase().includes(term) ||
        issue.description.toLowerCase().includes(term) ||
        issue.rating.toLowerCase().includes(term) ||
        issue.locationReviewed.toLocaleLowerCase().includes(term) ||
        issue.status.toLowerCase().includes(term),
    );
  }

  getRatingClass(rating: string): string {
    switch (rating?.toLowerCase()) {
      case 'red':       return 'rating-red';
      case 'yellow':    return 'rating-yellow';
      case 'blue':      return 'rating-blue';
      case 'green':     return 'rating-green';
      case 'not rated': return 'rating-not-rated';
      default:          return 'rating-not-rated';
    }
  }

  // ── Owner management ──
  onOwnerPersonSelected(person: IPeopleDetails): void {
    const email = person.InternetMail ?? person.EnterpriseId ?? '';
    if (email && !this.actionPlan.owners.includes(email)) {
      this.actionPlan.owners = [...this.actionPlan.owners, email];
    }
    this.ownerPickerValue = '';
  }

  removeOwner(owner: string): void {
    this.actionPlan.owners = this.actionPlan.owners.filter(o => o !== owner);
  }

  // ── Internal Dependency multiselect ──
  getFilteredInternalDependencyOptions(): string[] {
    const q = this.internalDependencySearch.toLowerCase();
    return this.internalDependencyOptions.filter(
      (o) => !this.actionPlan.internalDependencies.includes(o) && o.toLowerCase().includes(q),
    );
  }

  selectInternalDependency(dep: string): void {
    if (!this.actionPlan.internalDependencies.includes(dep)) {
      this.actionPlan.internalDependencies = [...this.actionPlan.internalDependencies, dep];
    }
    this.internalDependencySearch = '';
    this.internalDependencyDropdownOpen = false;
  }

  removeInternalDependency(dep: string): void {
    this.actionPlan.internalDependencies = this.actionPlan.internalDependencies.filter((d) => d !== dep);
  }

  closeInternalDependencyDropdown(): void {
    setTimeout(() => (this.internalDependencyDropdownOpen = false), 150);
  }

  openDatePicker(input: HTMLInputElement): void {
    this._activePicker = input;
    input?.showPicker?.();
  }

  // ── File management ──
  triggerFileInput(): void {
    this.fileInputRef.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const invalid: string[] = [];
    const valid: File[] = [];

    for (const file of Array.from(input.files)) {
      const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
      if (!this.allowedExtensions.includes(ext)) {
        invalid.push(`"${file.name}" has an unsupported file type.`);
      } else if (file.size > this.maxFileSizeBytes) {
        invalid.push(`"${file.name}" exceeds the 20 MB size limit.`);
      } else {
        valid.push(file);
      }
    }

    this.fileError = invalid.join(' ');
    this.filesToAdd = [...this.filesToAdd, ...valid];
    input.value = '';
  }

  removeNewFile(index: number): void {
    this.filesToAdd = this.filesToAdd.filter((_, i) => i !== index);
  }

  deleteDocument(doc: { documentId: number | null; fileName: string; storagePath: string }): void {
    const removed = this.actionPlan.uploadedDocuments.find(d => d.documentId === doc.documentId);
    if (removed) {
      this.deletedDocuments.push({ ...removed, isActive: false });
    }
    this.actionPlan.uploadedDocuments = this.actionPlan.uploadedDocuments.filter(
      d => d.documentId !== doc.documentId,
    );
  }

  downloadDocument(doc: { storagePath: string; fileName: string }): void {
    this.reviewerService.downloadIssueDocument(doc.storagePath)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = doc.fileName || doc.storagePath.split('/').pop() || 'download';
          a.click();
          URL.revokeObjectURL(url);
        },
        error: (err: any) => console.error('Download error:', err?.status, err?.message),
      });
  }

  // ── Update ──
  async update(): Promise<void> {
    if (this.isAdmin && this.linkedIssuesTouched) {
      if (this.isLoading) return;

      const toUnlink = this.linkedIssues.filter(i => !i.linked);
      if (toUnlink.length === 0) {
        sessionStorage.setItem('expandActionPlan', 'true');
        history.back();
        return;
      }

      this.isLoading = true;
      const reviewId = Number(this.reviewSummary.reviewId);
      const modifiedBy = this.rebarAuth.getUser() ?? '';

      // Use the IssueID direction (mirrors issue-view-edit which actually persists to DB)
      const calls = toUnlink.map(issue =>
        this.reviewerService.updateActionPlanLink({
          params: {
            category: 'UpdateActionPlanlink',
            ActionPlanID: null,
            IssueID: Number(issue.id.replace('I-', '')),
            ReviewID: reviewId,
            ModifiedBy: modifiedBy,
            RoleName: this.userRoleService.rollName ?? '',
          },
        })
      );

      forkJoin(calls)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            // Clear the issue management grid's persistent AP-link cache for each unlinked issue
            // so the grid falls back to fresh API data on the next load.
            toUnlink.forEach(issue => {
              const numericId = issue.id.replace(/^I-/i, '');
              try { localStorage.removeItem(`ast_linked_aps_${numericId}`); } catch {}
            });
            this.isLoading = false;
            this.reviewerService.notifyActionPlanSaved();
            try { localStorage.setItem('ast_ap_updated', Date.now().toString()); } catch {}
            window.close();
            history.back();
          },
          error: (err: any) => {
            console.error('UpdateActionPlanlink error:', err?.status, err?.message);
            this.isLoading = false;
          },
        });
      return;
    }

    if (!this.isDirty || this.isUpdating) return;

    if (!this.actionPlan.actionPlanStatus) {
      this.statusError = true;
      this.statusFieldRef?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (this.isClosedDateRequired && !this.actionPlan.closedDate) {
      this.closedDateError = true;
      return;
    }

    if (this.isTargetDateMandatory && !this.actionPlan.targetDate) {
      this.targetDateError = true;
      return;
    }

    this.isUpdating = true;

    const newDocuments = await Promise.all(
      this.filesToAdd.map(async (file) => {
        const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
        const fileContent = await this.readFileAsBase64(file);
        return {
          DocumentID: null,
          DocumentType: ext,
          UploadedBy: this.rebarAuth.getUser() ?? '',
          UploadedDate: new Date().toISOString(),
          IsActive: true,
          Description: '',
          FileName: file.name,
          StorageFilePath: `${this.reviewId}/${this.actionPlanId}/${file.name}`,
          fileContent,
          FileSize: file.size,
          FileType: ext,
        };
      }),
    );

    const existingDocuments = this.actionPlan.uploadedDocuments.map(d => ({
      DocumentID: d.documentId,
      DocumentType: d.documentType,
      UploadedBy: d.uploadedBy,
      UploadedDate: d.uploadedDate,
      IsActive: true,
      Description: d.description,
      FileName: d.fileName,
      StorageFilePath: d.storagePath,
      fileContent: null,
      FileSize: null,
      FileType: d.documentType,
    }));

    const deletedDocuments = this.deletedDocuments.map(d => ({
      DocumentID: d.documentId,
      DocumentType: d.documentType,
      UploadedBy: d.uploadedBy,
      UploadedDate: d.uploadedDate,
      IsActive: false,
      Description: d.description,
      FileName: d.fileName,
      StorageFilePath: d.storagePath,
      fileContent: null,
      FileSize: null,
      FileType: d.documentType,
    }));

    const allDocuments = [...existingDocuments, ...deletedDocuments, ...newDocuments];

    const fieldsJson: any[] = [
      { FieldName: 'AP_Description',       FieldValue: this.actionPlan.description ?? '',        DataType: 'NVARCHAR' },
      { FieldName: 'AP_OwnerEmail',         FieldValue: this.actionPlan.owners.join(','),          DataType: 'NVARCHAR' },
      { FieldName: 'AP_Status',             FieldValue: this.actionPlan.actionPlanStatus ?? '',    DataType: 'NVARCHAR' },
      { FieldName: 'AP_InternalDependency', FieldValue: this.actionPlan.internalDependencies.length ? this.actionPlan.internalDependencies.join(',') : null, DataType: 'NVARCHAR' },
      { FieldName: 'Internal dependency',    FieldValue: null,                                      DataType: 'NVARCHAR' },
      { FieldName: 'AP_RCComments',         FieldValue: this.actionPlan.rcComments ?? '',          DataType: 'NVARCHAR' },
      { FieldName: 'AP_ClientDependency',   FieldValue: this.actionPlan.clientDependency ?? '',    DataType: 'NVARCHAR' },
      ...(this.actionPlan.targetDate               ? [{ FieldName: 'AP_TargetDate',          FieldValue: this.actionPlan.targetDate.split('T')[0],               DataType: 'DATE' }] : []),
      ...(this.actionPlan.closedDate               ? [{ FieldName: 'AP_ClosedDate',          FieldValue: this.actionPlan.closedDate.split('T')[0],               DataType: 'DATE' }] : []),
      ...(this.actionPlan.revisedTargetDate
        ? [{ FieldName: 'AP_RevisedTargetDate',    FieldValue: this.actionPlan.revisedTargetDate,        DataType: 'DATE' }]
        : this.originalValues.revisedTargetDate
          ? [{ FieldName: 'AP_RevisedTargetDate',  FieldValue: '',                                       DataType: 'NVARCHAR' }]
          : []),
      ...(this.actionPlan.redClientObservationDate
        ? [{ FieldName: 'AP_RedClientDate',         FieldValue: this.actionPlan.redClientObservationDate, DataType: 'DATE' }]
        : this.originalValues.redClientObservationDate
          ? [{ FieldName: 'AP_RedClientDate',       FieldValue: '',                                       DataType: 'NVARCHAR' }]
          : []),
      ...(this.actionPlan.targetDateToInformClient
        ? [{ FieldName: 'AP_TargetDateClientInfo',  FieldValue: this.actionPlan.targetDateToInformClient, DataType: 'DATE' }]
        : this.originalValues.targetDateToInformClient
          ? [
              { FieldName: 'AP_TargetDateClientInfo', FieldValue: '', DataType: 'NVARCHAR' },
              { FieldName: 'TargetDateclientinfo',    FieldValue: '', DataType: 'NVARCHAR' },
            ]
          : []),
      { FieldName: 'LegacyAPNumber',         FieldValue: this.actionPlan.legacyApNumber ?? '',                   DataType: 'NVARCHAR' },
    ];

    const payload = {
      params: {
        category: 'UpdateActionPlan_EAV',
        ActionPlanID: Number(this.actionPlanId),
        ReviewID: Number(this.reviewSummary.reviewId),
        ActionPlanName: `AP-${this.actionPlanId}`,
        Status: this.actionPlan.actionPlanStatus,
        ModifiedBy: this.rebarAuth.getUserName() ?? '',
        RoleName: this.userRoleService.rollName ?? '',
        FieldsJSON: fieldsJson,
        DocumentsJSON: allDocuments,
      },
    };

    this.reviewerService.updateActionPlan(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.closeLinkedIssuesIfAllApsClosed().then(() => {
            this.isUpdating = false;
            this.reviewerService.notifyActionPlanSaved();
            try { localStorage.setItem('ast_ap_updated', Date.now().toString()); } catch {}
            try { localStorage.setItem('ast_issue_updated', Date.now().toString()); } catch {}
            this.showSuccessToast('Action Plan updated successfully.');
            this.toastTimer = setTimeout(() => { window.close(); history.back(); }, 2500);
          });
        },
        error: (err: any) => {
          console.error('UpdateActionPlan_EAV error:', err?.status, err?.message);
          this.isUpdating = false;
        },
      });
  }

  /**
   * After an AP is saved as Closed, closes every linked Open issue whose linked APs are now all Closed.
   * Failures are logged and swallowed so the AP save flow is never blocked.
   */
  private async closeLinkedIssuesIfAllApsClosed(): Promise<void> {
    if ((this.actionPlan.actionPlanStatus ?? '').toLowerCase() !== 'closed') return;

    const reviewId = Number(this.reviewSummary.reviewId);
    const openIssues = this.linkedIssues.filter(
      i => i.linked && (i.status ?? '').trim().toLowerCase() === 'open',
    );

    for (const issue of openIssues) {
      const issueNumericId = Number(issue.id.replace(/^I-/i, ''));
      try {
        const list: any = await firstValueFrom(
          this.reviewerService.getActionPlansEAV(reviewId, null, issueNumericId),
        );
        const plans: any[] = Array.isArray(list) ? list : [];
        // Guard against the API returning APs of the whole review: keep only APs linked to this issue.
        const linkedPlans = plans.filter(ap => {
          const ids = String(ap.LinkedIssueIDs ?? '').split(',').map(s => s.trim());
          const fieldIds = Array.isArray(ap.IssueFields) ? ap.IssueFields.map((f: any) => String(f.IssueID)) : [];
          return ids.includes(String(issueNumericId)) || fieldIds.includes(String(issueNumericId));
        });
        const allClosed = linkedPlans.length > 0 && linkedPlans.every(ap => {
          const status = String(String(ap.ActionPlanID) === String(this.actionPlanId)
            ? this.actionPlan.actionPlanStatus
            : (ap.Status || ap.AP_Status || ap.ActionPlanStatus || '')).toLowerCase();
          return status === 'closed';
        });
        if (!allClosed) continue;

        await firstValueFrom(this.reviewerService.updateIssueStatus({
          IssueID: issueNumericId,
          ReviewID: reviewId,
          IssueStatus: 'Closed',
          LoggedUserID: this.userRoleService.email,
          ReasonForReject: null,
        }));
        // Drop the issue page's cached AP list so it shows the refreshed statuses.
        try { localStorage.removeItem(`ast_ive_state_${issueNumericId}`); } catch {}
      } catch (err: any) {
        console.error('Auto-close issue error:', issue.id, err?.status, err?.message);
      }
    }
  }

  cancel(): void {
    window.close();
    // Fallback if browser prevents closing (tab not opened by script)
    history.back();
  }

  showSuccessToast(message: string): void {
    clearTimeout(this.toastTimer);
    this.toastMessage = message;
    this.showToast = true;
  }

  dismissToast(): void {
    clearTimeout(this.toastTimer);
    this.showToast = false;
  }

  formatDateDisplay(value: string): string {
    return this.dateFormatSvc.format(value);
  }

  private parseToInputDate(value: string): string {
    if (!value) return '';
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const MONTHS: Record<string, string> = {
      Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
      Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
    };
    const dmy = /^(\d{2})-([A-Za-z]{3})-(\d{4})$/.exec(value);
    if (dmy) return `${dmy[3]}-${MONTHS[dmy[2]] ?? '01'}-${dmy[1]}`;
    return '';
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.substring(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }
}